import React, { useState, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, Target, Heart, BookOpen, ArrowRight, Loader2 } from "lucide-react";

const PreAssessmentForm = ({ onComplete }) => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    interests: [],
    ambitions: "",
    preferred_field: "",
    favorite_subjects: [],
    additional_info: ""
  });

  const [interestsInput, setInterestsInput] = useState("");
  const [subjectsInput, setSubjectsInput] = useState("");

  const handleSubmit = async () => {
    // Validation
    if (!formData.ambitions || !formData.preferred_field) {
      toast.error("Please fill in all required fields");
      return;
    }

    const interests = interestsInput.split(',').map(s => s.trim()).filter(s => s);
    const subjects = subjectsInput.split(',').map(s => s.trim()).filter(s => s);

    if (interests.length === 0 || subjects.length === 0) {
      toast.error("Please provide at least one interest and one favorite subject");
      return;
    }

    try {
      setLoading(true);
      
      const data = {
        interests,
        ambitions: formData.ambitions,
        preferred_field: formData.preferred_field,
        favorite_subjects: subjects,
        additional_info: formData.additional_info
      };

      const response = await axios.post(`${API}/pre-assessment`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Pre-assessment completed! Generating personalized questions...");
      
      // Pass generated questions to parent component
      if (onComplete) {
        onComplete(response.data.questions);
      }
    } catch (error) {
      toast.error("Failed to submit pre-assessment");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block p-3 bg-indigo-100 rounded-full mb-4">
              <Lightbulb className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Tell Us About Yourself</h1>
            <p className="text-gray-600">
              Help us understand your interests and goals to provide personalized career recommendations
            </p>
            <Badge variant="outline" className="mt-3">
              This is a mandatory step • Takes 2-3 minutes
            </Badge>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Pre-Assessment Form</CardTitle>
              <CardDescription>
                Share your interests, ambitions, and preferences. We'll use AI to generate personalized assessment questions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Interests */}
              <div>
                <Label htmlFor="interests" className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  What are your interests? *
                </Label>
                <Input
                  id="interests"
                  value={interestsInput}
                  onChange={(e) => setInterestsInput(e.target.value)}
                  placeholder="e.g., Technology, Sports, Arts, Music, Science (comma-separated)"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">List your hobbies and things you enjoy</p>
              </div>

              {/* Career Ambitions */}
              <div>
                <Label htmlFor="ambitions" className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-500" />
                  What are your career ambitions? *
                </Label>
                <Textarea
                  id="ambitions"
                  value={formData.ambitions}
                  onChange={(e) => setFormData({...formData, ambitions: e.target.value})}
                  placeholder="Describe your career goals, what you want to achieve, and where you see yourself in the future..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Preferred Career Field */}
              <div>
                <Label htmlFor="preferred_field" className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-yellow-500" />
                  Which career field interests you most? *
                </Label>
                <Input
                  id="preferred_field"
                  value={formData.preferred_field}
                  onChange={(e) => setFormData({...formData, preferred_field: e.target.value})}
                  placeholder="e.g., Software Engineering, Medicine, Business, Design, Education"
                  className="mt-1"
                />
              </div>

              {/* Favorite Subjects */}
              <div>
                <Label htmlFor="subjects" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-blue-500" />
                  What are your favorite subjects? *
                </Label>
                <Input
                  id="subjects"
                  value={subjectsInput}
                  onChange={(e) => setSubjectsInput(e.target.value)}
                  placeholder="e.g., Mathematics, Physics, Literature, History (comma-separated)"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Subjects you excel at or enjoy learning</p>
              </div>

              {/* Additional Info */}
              <div>
                <Label htmlFor="additional_info">Additional Information (Optional)</Label>
                <Textarea
                  id="additional_info"
                  value={formData.additional_info}
                  onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                  placeholder="Any other information that might help us provide better recommendations..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Info Box */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-indigo-900 mb-1">How this helps</p>
                    <p className="text-sm text-indigo-700">
                      Based on your responses, our AI will generate 10 personalized assessment questions tailored to your interests and goals.
                      After completing the assessment, you'll receive specific career recommendations aligned with your profile.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Questions...
                  </>
                ) : (
                  <>
                    Continue to Assessment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PreAssessmentForm;
